import { useEffect, useState } from 'react';
import { useBbq } from '@/recentReports/bbq';
import { reportTypes } from '@/reports/types';
import { fetchReportById } from '@/reports/useReport/useReport';
import {
  useMediaQuery,
  Box,
  styled,
  Stack,
  Divider,
  useTheme,
} from '@/theme/index';
import { SearchNewUnclaimedMoneyForm } from '@/unclaimedMoney/index';
import { UnclaimedMoneyResultsDescription } from './UnclaimedMoneyDescription';
import { UnclaimedMoneyResultsInformation } from './UnclaimedMoneyResultsInformation';
import { UnclaimedMoneyHeader } from '../UnclaimedMoneyHeader/UnclaimedMoneyHeader';
import { UnclaimedMoneyReport } from '../UnclaimedMoneyReport';
import {
  UnclaimedMoneyReportItem,
  UnclaimedMoneyResultsProps,
} from '../interfaces';

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.customCard?.dark ?? '#F8F8F8',
  width: '100%',
  height: '100%',
  marginTop: 0,
  borderRadius: '5px',
  padding: `${theme.spacing(3)} ${theme.spacing(2)}`,

  [theme.breakpoints.up('sm')]: {
    marginTop: '105px',
  },

  [theme.breakpoints.up('md')]: {
    width: '1000px',
    marginTop: 0,
  },
}));

const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: theme.spacing(2),
  maxWidth: '100%',

  [theme.breakpoints.up('md')]: {
    marginTop: theme.spacing(15),
  },
}));

const StyledStack = styled(Stack)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  top: 0,
  alignItems: 'center',
  justifyContent: 'space-between',

  [theme.breakpoints.up('sm')]: {
    position: 'absolute',
    top: '70px',
  },

  [theme.breakpoints.up('md')]: {
    width: '1000px',
  },
}));

/**
 * This component is responsible for displaying the results of the unclaimed money search.
 * It shows the number of results found and the search criteria used.
 */
export function UnclaimedMoneyResults({
  permalink,
  isLoading,
  loadingImage,
  noResultsImage,
  mainReport,
  topImage,
  multiStateSearch,
  firstName,
  lastName,
  state,
  modalImage,
  stateList,
}: UnclaimedMoneyResultsProps) {
  const theme = useTheme();
  const { getReport } = useBbq();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isMultiStatesReport, setIsMultiStatesReport] = useState(false);
  const [loadingBbqObject, setLoadingBbqObject] = useState(true);
  const [unclaimedMoneyReports, setUnclaimedMoneyReports] = useState<
    UnclaimedMoneyReportItem[]
  >([]);

  const populateUnclaimedMoneyReportRowData = (
    report: UnclaimedMoneyReport
  ) => {
    return {
      rows: report.data.unclaimed_money_founds.map((item) => {
        return {
          name: item.name,
          address: item.address,
          reportedBy: item.reportedBy,
          heldIn: item.heldIn,
          amountHeld: item.amountHeld,
          reportedDate: item.reportedDate,
        };
      }),
    };
  };

  const getReportsFromPermalinks = async (permalinks: string[]) => {
    return Promise.all(
      permalinks.map(async (reportId: string) => {
        return await fetchReportById(reportId);
      })
    );
  };

  const createUnclaimedMoneyReportList = async (
    reports: UnclaimedMoneyReport[],
    permalinks: string[],
    states: string[]
  ) => {
    return reports.map((item: UnclaimedMoneyReport, index: number) => {
      return {
        permalink: permalinks[index],
        state: states[index],
        report: item,
        totalResults: item.data.unclaimed_money_founds.length,
        unclaimedMoneyRows: populateUnclaimedMoneyReportRowData(item),
      } as UnclaimedMoneyReportItem;
    });
  };

  useEffect(() => {
    if (!isLoading && mainReport && mainReport.data) {
      const firstReport = {
        permalink,
        state,
        report: mainReport,
        totalResults: mainReport.data.unclaimed_money_founds.length,
        unclaimedMoneyRows: populateUnclaimedMoneyReportRowData(mainReport),
      };
      setUnclaimedMoneyReports([firstReport]);

      if (!multiStateSearch) {
        setLoadingBbqObject(false);
        return;
      }

      setTimeout(() => {
        const params = {
          permalink: `${permalink}umms`,
          report_type: 'unclaimed_money_report',
        };
        getReport
          .mutateAsync(params)
          .then(async (response) => {
            if (response.success && response.reports.length) {
              const permalinksArray = Object.values(
                response.reports[0].permalinks
              ) as string[];
              const statesArray = Object.values(
                response.reports[0].states
              ) as string[];
              const permalinks = permalinksArray.slice(1);
              const states = statesArray.slice(1);
              await setIsMultiStatesReport(true);

              await getReportsFromPermalinks(permalinks)
                .then(async (rawReports) => {
                  const newReports: UnclaimedMoneyReport[] = await Promise.all(
                    rawReports.map(async (rawReport: reportTypes.Report) => {
                      return new UnclaimedMoneyReport(rawReport);
                    })
                  );
                  const reports: UnclaimedMoneyReportItem[] =
                    await createUnclaimedMoneyReportList(
                      newReports,
                      permalinks,
                      states
                    );
                  await setUnclaimedMoneyReports([firstReport, ...reports]);
                  setLoadingBbqObject(false);
                })
                .catch((error) => {
                  console.error(
                    'Error fetching reports from permalinks:',
                    error
                  );
                  setLoadingBbqObject(false);
                });
            } else {
              setLoadingBbqObject(false);
            }
          })
          .catch((error) => {
            console.error('Error fetching multi-state report:', error);
            setLoadingBbqObject(false);
          });
      }, 2000);
    }
  }, [isLoading, mainReport, multiStateSearch, permalink, state]);

  return (
    <PageContainer>
      <StyledStack direction="row">
        <Box marginBottom={1.25}>
          <UnclaimedMoneyHeader />
        </Box>
        {!isMobile && <img src={topImage} alt="Robot with money" />}
      </StyledStack>
      <StyledBox>
        <SearchNewUnclaimedMoneyForm
          fname={firstName}
          lname={lastName}
          stateList={stateList}
        />
        <Divider
          sx={{
            marginTop: `${theme.spacing(1.5)}`,
            marginBottom: `${theme.spacing(2.5)}`,
          }}
        />

        {isLoading || loadingBbqObject ? (
          <UnclaimedMoneyResultsInformation
            informationImage={loadingImage}
            title="Searching our database"
            subTitle="This may take a couple of minutes"
          />
        ) : (
          <UnclaimedMoneyResultsDescription
            firstName={firstName}
            lastName={lastName}
            bodyImage={modalImage}
            noResultsImage={noResultsImage}
            unclaimedMoneyReports={unclaimedMoneyReports}
            states={stateList ?? []}
            isMultiStatesReport={isMultiStatesReport}
            loadingBbqObject={loadingBbqObject}
          />
        )}
      </StyledBox>
    </PageContainer>
  );
}
